import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@akaike/ui";
import { IconCollapse, IconExpand } from "./icons";

// Interactive bounding-box picker for the merchant-sweep "Region scope". The four
// number inputs in JobEditor stay the source of truth; this map two-way-binds to
// them: drag/resize the rectangle -> the inputs update; type into the inputs -> the
// rectangle follows. Raw Leaflet (the only map dep, see RunCoverageMap): the
// rectangle is editable via four corner handles (resize) + a centre handle (move),
// so we avoid pulling in leaflet-draw / leaflet-editable.

// minLat, minLon, maxLat, maxLon: mirrors JobEditor's BboxForm exactly.
export type Bbox4 = [string, string, string, string];

// Mainland-India bbox as form strings, the default scope for a new merchant sweep.
export const INDIA_BBOX_FORM: Bbox4 = ["6.5", "68.1", "37.1", "97.4"];

const INDIA: L.LatLngTuple = [22.6, 80.0];
// Mainland-India bounding box (min lat/lon -> max lat/lon). Used as the default box.
const INDIA_BBOX: Box = { s: 6.5, w: 68.1, n: 37.1, e: 97.4 };
const BRAND = "#c2870f";

type Box = { s: number; w: number; n: number; e: number };

function parseBbox(b: Bbox4): Box | null {
  const t = b.map((x) => x.trim());
  if (t.some((x) => x === "")) return null;
  const [s, w, n, e] = t.map(Number);
  if (![s, w, n, e].every(Number.isFinite)) return null;
  if (s >= n || w >= e) return null;
  return { s, w, n, e };
}

const r = (n: number) => String(Math.round(n * 1e4) / 1e4); // ~11 m precision
function boundsToBbox(b: L.LatLngBounds): Bbox4 {
  return [r(b.getSouth()), r(b.getWest()), r(b.getNorth()), r(b.getEast())];
}
function boxBounds(p: Box): L.LatLngBounds {
  return L.latLngBounds([p.s, p.w], [p.n, p.e]);
}
function eq(a: L.LatLngBounds, b: L.LatLngBounds): boolean {
  const e = 1e-4;
  return (
    Math.abs(a.getSouth() - b.getSouth()) < e &&
    Math.abs(a.getWest() - b.getWest()) < e &&
    Math.abs(a.getNorth() - b.getNorth()) < e &&
    Math.abs(a.getEast() - b.getEast()) < e
  );
}

type CornerKey = "nw" | "ne" | "se" | "sw";
const CORNERS: CornerKey[] = ["nw", "ne", "se", "sw"];
const OPPOSITE: Record<CornerKey, CornerKey> = {
  nw: "se",
  se: "nw",
  ne: "sw",
  sw: "ne",
};
const CURSOR: Record<CornerKey, string> = {
  nw: "nwse-resize",
  se: "nwse-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
};

function corner(b: L.LatLngBounds, k: CornerKey): L.LatLng {
  switch (k) {
    case "nw":
      return L.latLng(b.getNorth(), b.getWest());
    case "ne":
      return L.latLng(b.getNorth(), b.getEast());
    case "se":
      return L.latLng(b.getSouth(), b.getEast());
    case "sw":
      return L.latLng(b.getSouth(), b.getWest());
  }
}

function cornerIcon(k: CornerKey): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<div style="width:12px;height:12px;background:#fff;border:2px solid ${BRAND};border-radius:3px;box-shadow:0 1px 3px rgba(0,0,0,.4);cursor:${CURSOR[k]}"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}
const MOVE_ICON = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;background:${BRAND};border:2px solid #fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,.4);cursor:move"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Edge handles sit at the midpoint of each side and resize that one side only.
type EdgeKey = "n" | "e" | "s" | "w";
const EDGES: EdgeKey[] = ["n", "e", "s", "w"];
const EDGE_CURSOR: Record<EdgeKey, string> = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
};

function edge(b: L.LatLngBounds, k: EdgeKey): L.LatLng {
  const c = b.getCenter();
  switch (k) {
    case "n":
      return L.latLng(b.getNorth(), c.lng);
    case "s":
      return L.latLng(b.getSouth(), c.lng);
    case "e":
      return L.latLng(c.lat, b.getEast());
    case "w":
      return L.latLng(c.lat, b.getWest());
  }
}

function edgeIcon(k: EdgeKey): L.DivIcon {
  const horiz = k === "n" || k === "s"; // a wide bar on top/bottom, a tall bar on the sides
  const w = horiz ? 16 : 8;
  const h = horiz ? 8 : 16;
  return L.divIcon({
    className: "",
    html: `<div style="width:${w}px;height:${h}px;background:#fff;border:2px solid ${BRAND};border-radius:3px;box-shadow:0 1px 3px rgba(0,0,0,.4);cursor:${EDGE_CURSOR[k]}"></div>`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h / 2],
  });
}

// Read-only counterpart to BboxMapPicker: draws a region bbox rectangle the user
// can pan/zoom around but not edit. Used on the job detail page's Settings view so
// operators can explore a merchant sweep's region scope without changing it.
// `bbox` is [minLat, minLon, maxLat, maxLon]; null falls back to all-India.
export function BboxMapView({
  bbox,
  height = 240,
}: {
  bbox: number[] | null;
  height?: number;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const box: Box =
    bbox && bbox.length === 4
      ? { s: bbox[0], w: bbox[1], n: bbox[2], e: bbox[3] }
      : INDIA_BBOX;
  const key = `${box.s},${box.w},${box.n},${box.e}`;

  useEffect(() => {
    if (!elRef.current) return;
    const bounds = boxBounds(box);
    // Fully pannable / zoomable, only the bbox rectangle is fixed (read-only).
    const map = L.map(elRef.current, {
      attributionControl: false,
      worldCopyJump: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);
    L.rectangle(bounds, {
      color: BRAND,
      weight: 2,
      fillColor: BRAND,
      fillOpacity: 0.1,
    }).addTo(map);
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 13 });
    mapRef.current = map;
    const t0 = setTimeout(() => map.invalidateSize(), 0);
    const t1 = setTimeout(() => map.invalidateSize(), 200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      map.remove();
      mapRef.current = null;
    };
    // re-create on bbox change (rare); `box` is derived from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, height]);

  return (
    <div
      ref={elRef}
      style={{
        width: "100%",
        height,
        borderRadius: 6,
        overflow: "hidden",
        zIndex: 0,
      }}
    />
  );
}

export function BboxMapPicker({
  bbox,
  onChange,
}: {
  bbox: Bbox4;
  onChange: (b: Bbox4) => void;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const rectRef = useRef<L.Rectangle | null>(null);
  const handlesRef = useRef<Partial<Record<CornerKey, L.Marker>>>({});
  const edgesRef = useRef<Partial<Record<EdgeKey, L.Marker>>>({});
  const centerRef = useRef<L.Marker | null>(null);
  const moveAnchorRef = useRef<L.LatLng | null>(null);
  // True while we're emitting our own edit, so the bbox-prop effect doesn't fight
  // the live drag by re-setting the bounds underneath the cursor.
  const selfEditRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Pop the map into a large overlay (above the job modal) for precise placement.
  const [expanded, setExpanded] = useState(false);

  // -- draw / update the rectangle + its handles from a bounds ------------------
  // `skip` is the handle currently being dragged: we leave it under the cursor.
  function render(b: L.LatLngBounds, skip?: CornerKey | EdgeKey | "center"): void {
    const map = mapRef.current;
    if (!map) return;
    if (!rectRef.current) {
      rectRef.current = L.rectangle(b, {
        color: BRAND,
        weight: 2,
        fillColor: BRAND,
        fillOpacity: 0.08,
      }).addTo(map);
    } else {
      rectRef.current.setBounds(b);
    }
    for (const k of CORNERS) {
      const pos = corner(b, k);
      let m = handlesRef.current[k];
      if (!m) {
        m = L.marker(pos, {
          icon: cornerIcon(k),
          draggable: true,
          zIndexOffset: 1000,
        }).addTo(map);
        m.on("drag", () => onCornerDrag(k));
        m.on("dragend", emit);
        handlesRef.current[k] = m;
      } else if (skip !== k) {
        m.setLatLng(pos);
      }
    }
    for (const k of EDGES) {
      const pos = edge(b, k);
      let m = edgesRef.current[k];
      if (!m) {
        m = L.marker(pos, {
          icon: edgeIcon(k),
          draggable: true,
          zIndexOffset: 1000,
        }).addTo(map);
        m.on("drag", () => onEdgeDrag(k));
        m.on("dragend", emit);
        edgesRef.current[k] = m;
      } else if (skip !== k) {
        m.setLatLng(pos);
      }
    }
    const c = b.getCenter();
    if (!centerRef.current) {
      const cm = L.marker(c, {
        icon: MOVE_ICON,
        draggable: true,
        zIndexOffset: 900,
      }).addTo(map);
      cm.on("dragstart", () => (moveAnchorRef.current = cm.getLatLng()));
      cm.on("drag", onCenterDrag);
      cm.on("dragend", emit);
      centerRef.current = cm;
    } else if (skip !== "center") {
      centerRef.current.setLatLng(c);
    }
  }

  function onCornerDrag(k: CornerKey): void {
    const m = handlesRef.current[k];
    const rect = rectRef.current;
    if (!m || !rect) return;
    const opp = corner(rect.getBounds(), OPPOSITE[k]); // diagonally-opposite corner stays put
    render(L.latLngBounds([opp, m.getLatLng()]), k);
    emit();
  }

  function onEdgeDrag(k: EdgeKey): void {
    const m = edgesRef.current[k];
    const rect = rectRef.current;
    if (!m || !rect) return;
    const ll = m.getLatLng();
    const bd = rect.getBounds();
    let s = bd.getSouth();
    let w = bd.getWest();
    let n = bd.getNorth();
    let e = bd.getEast();
    // Move only the dragged side; the perpendicular extent stays fixed.
    if (k === "n") n = ll.lat;
    else if (k === "s") s = ll.lat;
    else if (k === "e") e = ll.lng;
    else w = ll.lng;
    render(L.latLngBounds([s, w], [n, e]), k);
    emit();
  }

  function onCenterDrag(): void {
    const cm = centerRef.current;
    const rect = rectRef.current;
    const anchor = moveAnchorRef.current;
    if (!cm || !rect || !anchor) return;
    const now = cm.getLatLng();
    const dLat = now.lat - anchor.lat;
    const dLng = now.lng - anchor.lng;
    moveAnchorRef.current = now;
    const bd = rect.getBounds();
    render(
      L.latLngBounds(
        [bd.getSouth() + dLat, bd.getWest() + dLng],
        [bd.getNorth() + dLat, bd.getEast() + dLng],
      ),
      "center",
    );
    emit();
  }

  function emit(): void {
    const rect = rectRef.current;
    if (!rect) return;
    selfEditRef.current = true;
    onChangeRef.current(boundsToBbox(rect.getBounds()));
  }

  function clearBox(): void {
    rectRef.current?.remove();
    rectRef.current = null;
    for (const k of CORNERS) handlesRef.current[k]?.remove();
    handlesRef.current = {};
    for (const k of EDGES) edgesRef.current[k]?.remove();
    edgesRef.current = {};
    centerRef.current?.remove();
    centerRef.current = null;
  }

  function drawDefault(): void {
    const map = mapRef.current;
    if (!map) return;
    const b = boxBounds(INDIA_BBOX);
    selfEditRef.current = true;
    render(b);
    map.fitBounds(b, { padding: [20, 20] });
    onChangeRef.current(boundsToBbox(b));
  }

  function clearAll(): void {
    clearBox();
    selfEditRef.current = true;
    onChangeRef.current(["", "", "", ""]);
  }

  // -- create the map once ------------------------------------------------------
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      attributionControl: false,
      worldCopyJump: true,
    }).setView(INDIA, 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    const p = parseBbox(bbox);
    if (p) {
      render(boxBounds(p));
      map.fitBounds(boxBounds(p), { padding: [40, 40], maxZoom: 13 });
    }
    // Settle the size once the modal has laid out (and again after any open anim).
    const t0 = setTimeout(() => map.invalidateSize(), 0);
    const t1 = setTimeout(() => map.invalidateSize(), 200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      map.remove();
      mapRef.current = null;
      rectRef.current = null;
      handlesRef.current = {};
      edgesRef.current = {};
      centerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- follow the inputs when THEY change (not our own drag emits) --------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (selfEditRef.current) {
      selfEditRef.current = false;
      return;
    }
    const p = parseBbox(bbox);
    if (!p) {
      clearBox();
      return;
    }
    const b = boxBounds(p);
    const cur = rectRef.current?.getBounds();
    if (!cur || !eq(cur, b)) render(b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox]);

  // The map container resized (inline <-> overlay): let Leaflet re-read its size.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const t = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(t);
  }, [expanded]);

  // While expanded, Esc collapses the overlay instead of closing the whole job
  // modal. Capture phase + stopImmediatePropagation preempts JobEditor's handler.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [expanded]);

  const hasBox = parseBbox(bbox) !== null;

  return (
    <>
      {/* Backdrop + overlay are always mounted; only styles toggle, so the single
          Leaflet container (elRef) never remounts when expanding. */}
      <div
        onClick={() => setExpanded(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.5)",
          zIndex: 3900,
          display: expanded ? "block" : "none",
        }}
      />
      <div
        className="col"
        style={
          expanded
            ? {
                position: "fixed",
                inset: 32,
                zIndex: 4000,
                gap: 8,
                padding: 12,
                background: "var(--panel)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                boxShadow: "0 12px 48px rgba(0,0,0,.45)",
              }
            : { gap: 8 }
        }
      >
        <div
          style={{
            position: "relative",
            flex: expanded ? 1 : "none",
            minHeight: 0,
          }}
        >
          <div
            ref={elRef}
            style={{
              width: "100%",
              height: expanded ? "100%" : 300,
              borderRadius: 6,
              overflow: "hidden",
              zIndex: 0,
            }}
          />
          <Button
            size="sm"
            type="button"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Collapse map" : "Expand map for precise placement"}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 500,
              boxShadow: "0 2px 8px rgba(0,0,0,.3)",
            }}
          >
            {expanded ? <IconCollapse /> : <IconExpand />}
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </div>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          {hasBox ? (
            <Button variant="outline" type="button" onClick={clearAll}>
              Clear box: sweep all of India
            </Button>
          ) : (
            <Button variant="outline" type="button" onClick={drawDefault}>
              Draw default box (all India)
            </Button>
          )}
          <span className="hint">
            {hasBox
              ? "Drag the centre dot to move, a corner to resize both sides, or an edge to resize one side. The values below stay in sync."
              : "Draw the default all-India box, then drag its corners to scope down to your region."}
          </span>
        </div>
      </div>
    </>
  );
}
